import React, { useState } from 'react';
import { CSVLink } from 'react-csv';
import { blueDark01 } from '../../../styles/colors';
import { CloseIcon, Text } from '../../../styles/common.styles';
import ModalFooter from '../../common/modalFooter/ModalFooter';
import ModalTitle from '../../common/modalTitle/ModalTitle';
import { FavoriteCSVHeader } from '../../projects/projectActions/exportProject/ExportProject.mockData';
import {
    ExportFavoriteToCSVForm,
    ExportProjectModal,
    ExportSpin,
} from './ExportFavoriteToCSV.styles';
import { ExternalProduct, Product } from '../../../models/product/types';
import { FavoriteType } from '../../../models/product/enums';
import { useAppSelector, useAppDispatch } from '../../../hooks/redux';
import { mapLowEmittingData } from '../../../helpers/lowEmittingHelper';
import {
    fetchTagsInBatches,
    ProjectProductTagLike,
} from '../../../helpers/projectProductTagBatchHelper';
import {
    collectDisclosuresFromProducts,
    fetchDisclosureImpactAreaMap,
    getProductImpactAreas,
    impactAreasToCSVString,
} from '../../../helpers/cmfImpactAreaMapper';

interface Props {
    isOpen: boolean;
    products: Array<ExternalProduct>;
    favoriteType: FavoriteType;
    closeModal: () => void;
}

interface State {
    exportData: Array<any>;
    fileName: string;
    pending: boolean;
}

const ExportFavoriteToCSV: React.FC<Props> = ({ isOpen, products, favoriteType, closeModal }) => {
    const selector = useAppSelector((combinedState) => combinedState);
    const dispatch = useAppDispatch();
    const { authReducer } = selector;

    const [state, setState] = useState<State>({
        exportData: [] as any[],
        fileName: 'export products',
        pending: false,
    });

    const onSubmit = async () => {
        setState({ ...state, pending: true });

        await mapLowEmittingData(products as Product[], dispatch);

        const projectProductIds = products.map((p) => (p as any).projectProductId).filter(Boolean);
        const allTags: ProjectProductTagLike[] = await fetchTagsInBatches(dispatch, projectProductIds);
        const tagsByProjectProductId = allTags.reduce((acc: Record<number, string[]>, tag: ProjectProductTagLike) => {
            if (!acc[tag.projectProductId]) {
                acc[tag.projectProductId] = [];
            }
            const tagName = String(tag.tag?.name ?? tag.name ?? '').trim();
            if (tagName.length > 0) {
                acc[tag.projectProductId].push(tagName);
            }
            return acc;
        }, {} as Record<number, string[]>);

        // Collect all unique disclosures from products
        const allDisclosures = collectDisclosuresFromProducts(products);

        // Fetch impact areas from API
        const disclosureImpactAreaMap = await fetchDisclosureImpactAreaMap(
            dispatch,
            allDisclosures,
        );

        // Map products with CMF impact areas
        const currentProducts = products.map((p) => {
            const impactAreas = getProductImpactAreas(p, disclosureImpactAreaMap);

            return {
                ...p,
                masterFormat: `${p.mfSection.number} ${p.mfSection.label}`,
                tagsCSV: (tagsByProjectProductId[(p as any).projectProductId] ?? []).join(', '),
                // CMF Impact Areas - one comma-separated column per disclosure
                epdCmfImpactAreas: impactAreasToCSVString(impactAreas.epd),
                hpdCmfImpactAreas: impactAreasToCSVString(impactAreas.hpd),
                declareCmfImpactAreas: impactAreasToCSVString(impactAreas.declare),
                c2cCmfImpactAreas: impactAreasToCSVString(impactAreas.c2c),
                c2cMHCCmfImpactAreas: impactAreasToCSVString(impactAreas.c2cMHC),
                lpcCmfImpactAreas: impactAreasToCSVString(impactAreas.lpc),
                cleanAirSilverCmfImpactAreas: impactAreasToCSVString(impactAreas.cleanAirSilver),
                cleanAirGoldCmfImpactAreas: impactAreasToCSVString(impactAreas.cleanAirGold),
                clearchemCmfImpactAreas: impactAreasToCSVString(impactAreas.clearChem),
                floorscoreCmfImpactAreas: impactAreasToCSVString(impactAreas.floorscore),
                greenCertifiedCmfImpactAreas: impactAreasToCSVString(impactAreas.greenCertified),
                greenguardCmfImpactAreas: impactAreasToCSVString(impactAreas.greenguard),
                greenguardGoldCmfImpactAreas: impactAreasToCSVString(impactAreas.greenguardGold),
                indoorAdvantageCmfImpactAreas: impactAreasToCSVString(impactAreas.indoorAdvantage),
                indoorAdvantageGoldCmfImpactAreas: impactAreasToCSVString(impactAreas.indoorAdvantageGold),
            };
        });

        setTimeout(() => {
            setState({ ...state, exportData: currentProducts, pending: false });
            const link = document.getElementById('csv-link') as any;
            link.click();
            closeModal();
        }, 500);
    };

    return (
        <ExportProjectModal
            open={isOpen}
            onCancel={closeModal}
            closeIcon={<CloseIcon />}
            centered
            footer={null}
        >
            <ExportSpin spinning={state.pending}>
                <ModalTitle title="Export to CSV" icon="ic-modal-title-export.svg" />
                <div>
                    <Text weight={400} size={15} height={20} color={blueDark01}>
                        Export to CSV includes a spreadsheet of product name, manufacturer name,
                        MasterFormat number, URL to their Transparency Catalog listing page,
                        optionally added attributes (pricing, BOD, substitutions), and the types
                        of transparency documentation for that product.
                    </Text>
                    <CSVLink
                        id="csv-link"
                        filename={
                            favoriteType === FavoriteType.MyFavorite
                                ? `${authReducer.user?.firstName} ${authReducer.user?.lastName}'s favorite products`
                                : `${authReducer.user?.office.name}' favorite products`
                        }
                        data={state.exportData}
                        headers={FavoriteCSVHeader}
                    >
                        Download me
                    </CSVLink>
                </div>
                <ExportFavoriteToCSVForm onFinish={onSubmit}>
                    <ModalFooter action="Export" cancel={closeModal} submit={() => { }} cancelText="Cancel" />
                </ExportFavoriteToCSVForm>
            </ExportSpin>
        </ExportProjectModal>
    );
};

export default ExportFavoriteToCSV;
